import { useEffect, useState } from 'react';
import { SectionWrapper } from '../../../../components/ui/SectionWrapper';
import { TechBadge } from '../../../../components/ui/TechBadge';
import { useLanguage } from '../../../../context/LanguageContext';
import { WorkExperience } from '../../../../models/work.experience.interface';
import { workExperiencesService } from '../../../../services/work-experiences.service';

export function formatDate(date: Date | null, presentLabel: string): string {
    if (!date) return presentLabel;
    return `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

export function ExperienceSection() {
    const { lang, t } = useLanguage();
    const [experiences, setExperiences] = useState<WorkExperience[]>([]);

    useEffect(() => {
        setExperiences(workExperiencesService.getWorkExperiences());
    }, []);

    return (
        <SectionWrapper id="experience" title={t('section.experience')}>
            <div className="relative">
                <div className="absolute left-4 md:left-6 top-0 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700" />

                <div className="space-y-8">
                    {experiences.map((exp, index) => (
                        <div key={exp.id} className="relative pl-12 md:pl-16">
                            <div className={`absolute left-2.5 md:left-4.5 top-2 w-3 h-3 rounded-full ${index === 0 ? 'bg-blue-600 dark:bg-blue-400 ring-4 ring-blue-600/20 dark:ring-blue-400/20 animate-pulse' : 'bg-blue-600 dark:bg-blue-400'}`} />

                            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm ring-1 ring-slate-900/5 dark:ring-slate-700/50">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                                        {exp.role.name[lang]}
                                    </h3>
                                    <span className="text-sm text-slate-500 dark:text-slate-400 font-mono">
                                        {formatDate(exp.start_date, t('label.present'))} - {formatDate(exp.end_date, t('label.present'))}
                                    </span>
                                </div>

                                <p className="text-blue-600 dark:text-blue-400 font-medium text-sm mb-3">
                                    {exp.company.name}
                                </p>

                                <p className="text-slate-600 dark:text-slate-300 mb-3">
                                    {exp.description[lang]}
                                </p>

                                {exp.bullets.length > 0 && (
                                    <ul className="list-disc ml-4 space-y-1 text-sm text-slate-600 dark:text-slate-300 mb-4">
                                        {exp.bullets.map((bullet, i) => (
                                            <li key={i}>{bullet[lang]}</li>
                                        ))}
                                    </ul>
                                )}

                                {exp.technologies.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {exp.technologies.map(tech => (
                                            <TechBadge key={tech.id} name={tech.name} size="sm" />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </SectionWrapper>
    );
}
